import React, { useEffect, useRef, useState } from "react";
import { CheckCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useAntdTheme } from "../../shared/hooks";
import { useErrorNotification } from "./hooks/useErrorNotification";
import { useOrderPriorityButton } from "./hooks/useOrderPriorityButton";
import { useResponsiveFontSize } from "./hooks/useResponsiveFontSize";

import ButtonWithLoading from "../../shared/components/buttons/ButtonWithLoading";
import { BaseOrderColumn } from "./BaseOrderColumn";
import { printOnStartOrder } from "../../shared/utils/Print";

import { hasNewOrders, API_URL } from "../../shared/utils/Util";
import "../../styles/PendingOrderCol.css";
import notificationSound from "../../assets/notification.mp3";

const notificationAudio = new Audio(notificationSound);

interface PendingOrderColProps {
  orders: any[];
  renderOrder: (order: any) => React.ReactNode;
  renderOrderTitle?: (order: any) => React.ReactNode;
  renderOrderDetails: (order: any) => React.ReactNode;
  restaurantId: string;
  showHeading?: boolean;
  elemsMaxHeight?: string;
  playSound?: boolean;
  bgColor?: string;
}

const PendingOrderCol: React.FC<PendingOrderColProps> = ({
  orders,
  renderOrder,
  renderOrderTitle,
  renderOrderDetails,
  restaurantId,
  showHeading,
  elemsMaxHeight,
  playSound,
  bgColor,
}) => {
  const prevOrders = useRef<any[]>([]);
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { showError } = useErrorNotification();
  const { renderStarButton } = useOrderPriorityButton({ restaurantId, orders });
  const { getButtonFontSize, getButtonMinWidth } = useResponsiveFontSize();

  useEffect(() => {
    if (playSound && hasNewOrders(prevOrders.current, orders)) {
      const Android = (window as any).Android;
      if (Android) {
        Android.playSound();
      } else {
        notificationAudio.play().catch((err) => console.error(err));
      }
    }
    prevOrders.current = orders;
  }, [orders, playSound]);

  return (
    <BaseOrderColumn
      orders={orders}
      renderOrder={renderOrder}
      renderOrderTitle={renderOrderTitle}
      renderOrderDetails={renderOrderDetails}
      renderLeftButton={renderStarButton}
      renderRightButton={(order: any, orderIndex: number) => (
        <StartButton
          restaurantId={restaurantId}
          order={order}
          t={t}
          token={token}
          showError={showError}
          fontSize={getButtonFontSize()}
          minWidth={getButtonMinWidth()}
        />
      )}
      emptyDescription={t("no_orders")}
      showHeading={showHeading}
      elemsMaxHeight={elemsMaxHeight}
      modalCentered={true}
      modalZIndex={1050}
      useCustomModalStyles={true}
    />
  );
};

interface StartButtonProps {
  restaurantId: string;
  order: any;
  t: any;
  token: any;
  showError: (messageKey?: string, descriptionKey?: string) => void;
  fontSize: string;
  minWidth: string;
}

// StartButton optimistically disables itself and shows a check mark once the
// start request succeeds, instead of waiting for the websocket to move the
// order out of the pending column. This closes the window where a second tap
// could hit the backend with an already-started order ("not in pending state"
// 400). When the websocket eventually removes the order from the pending list,
// this component unmounts and the local state is discarded.
const StartButton: React.FC<StartButtonProps> = ({
  restaurantId,
  order,
  t,
  token,
  showError,
  fontSize,
  minWidth,
}) => {
  const [started, setStarted] = useState(false);

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        alignItems: "stretch",
      }}
    >
      <ButtonWithLoading
        disabled={started}
        style={{
          flex: 1,
          minWidth: minWidth,
          fontSize: fontSize,
          fontWeight: 600,
          borderRadius: 0,
          height: "100%",
          background: started ? token.colorSuccess : token.colorPrimary,
          borderColor: started ? token.colorSuccess : token.colorPrimary,
          color: token.colorWhite,
          transition: "all 0.2s",
        }}
        postURL={`${API_URL}/v1/r/${restaurantId}/start_orders`}
        postBody={{
          order_ids: [order.id],
        }}
        postHandleResponse={() => {
          setStarted(true);
          printOnStartOrder(order, () => {
            showError("unable_to_print");
          });
        }}
        postHandleError={() => {
          showError();
        }}
      >
        {started ? <CheckCircleOutlined /> : t("start_button")}
      </ButtonWithLoading>
    </div>
  );
};

export default PendingOrderCol;
